import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { ReactElement, useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { useHttpClient } from '../../providers/HttpClientProvider';

interface CachedShareLink {
  url: string;
  expiresAt: string;
}

const driverShareLinkMemoryCache = new Map<number, CachedShareLink>();

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 450,
  minHeight: 450,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

interface QrModalProps {
  id: string;
  routeName: string;
  routeId: number;
  Icon?: ReactElement;
}

export function QRModal(props: QrModalProps) {
  const [open, setOpen] = useState(false);
  const { id, routeName, routeId, Icon } = props;
  const { httpClient } = useHttpClient();
  const normalizedRouteId = Number(routeId);

  const [tempUrl, setTempUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);

  const getCachedShareLink = (): CachedShareLink | null => {
    if (!Number.isInteger(normalizedRouteId) || normalizedRouteId <= 0) {
      return null;
    }

    const cached = driverShareLinkMemoryCache.get(normalizedRouteId);
    if (!cached) {
      return null;
    }

    const expiresAtTs = new Date(cached.expiresAt).getTime();
    if (!cached.url || Number.isNaN(expiresAtTs) || expiresAtTs <= Date.now()) {
      driverShareLinkMemoryCache.delete(normalizedRouteId);
      return null;
    }

    return cached;
  };

  const saveCachedShareLink = (link: CachedShareLink) => {
    if (!Number.isInteger(normalizedRouteId) || normalizedRouteId <= 0) {
      return;
    }

    driverShareLinkMemoryCache.set(normalizedRouteId, link);
  };

  useEffect(() => {
    if (!open || loading || hasRequested) {
      return;
    }

    const cached = getCachedShareLink();
    if (cached) {
      setTempUrl(cached.url);
      setHasRequested(true);
      return;
    }

    setHasRequested(true);
  }, [open, loading, hasRequested]);

  useEffect(() => {
    if (!open) {
      setTempUrl(null);
      setLoading(false);
      setError(null);
      setCopied(false);
      setHasRequested(false);
    }
  }, [open]);

  const generateShareLink = async () => {
    if (!Number.isInteger(normalizedRouteId) || normalizedRouteId <= 0) {
      setError(
        'Unable to generate a share link because this route has no valid ID.',
      );
      setHasRequested(true);
      return;
    }

    setHasRequested(true);
    setLoading(true);
    setError(null);
    setTempUrl(null); // Clear the old URL from state to ensure no stale data

    // Clear in-memory cache before generating a new link to ensure a fresh URL is fetched
    driverShareLinkMemoryCache.delete(normalizedRouteId);

    try {
      const response = await httpClient.generateRouteShareLink({
        routeId: normalizedRouteId,
        ttlHours: 24,
      });

      const url = response?.data?.data?.url;
      const expiresAt = response?.data?.data?.expiresAt;
      if (url) {
        setTempUrl(url);
        if (expiresAt) {
          saveCachedShareLink({
            url,
            expiresAt,
          });
        }
      } else {
        setError('Failed to generate share link');
      }
    } catch (_err) {
      setError('Error generating share link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!tempUrl) {
      return;
    }

    await navigator.clipboard.writeText(tempUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <Button onClick={() => setOpen(true)}>
        {Icon || null}
        <QrCode2Icon />
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography id="modal-modal-description" sx={{ mt: 1 }}>
              Route Name: {routeName}
            </Typography>
            <Typography id="modal-modal-description" sx={{ mt: 1 }}>
              Route ID: {routeId}
            </Typography>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              {id}
            </Typography>
          </Box>

          {error && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{error}</Alert>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setError(null);
                    void generateShareLink();
                  }}
                >
                  Retry
                </Button>
              </Box>
            </Box>
          )}

          {!error && !loading && !tempUrl && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={() => void generateShareLink()}
              >
                Generate Link
              </Button>
            </Box>
          )}

          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 2,
                height: '200px',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 2,
              }}
            >
              {!error && (
                <>
                  {tempUrl && (
                    <QRCode
                      size={512}
                      style={{ height: '200px', width: '200px' }}
                      value={tempUrl}
                      viewBox="0 0 256 256"
                    />
                  )}

                  {tempUrl && (
                    <>
                      <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
                        <IconButton
                          onClick={() => void copyToClipboard()}
                          sx={{ ml: 2, height: '40px', width: '40px' }}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Button
                        variant="outlined"
                        sx={{ ml: 1.5 }}
                        onClick={() => void generateShareLink()}
                      >
                        Refresh Link
                      </Button>
                    </>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      </Modal>
    </div>
  );
}
